import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, RefreshCw } from 'lucide-react';
import { Block, TradingPlatform, AssetClass } from '../../types';
import { saveStrategy, updateStrategy, isTemplateAuthor } from '../../services/strategyService';
import { updateTemplate, copyTemplate, getTemplate } from '../../services/templateService';
import { useAuth } from '../../contexts/AuthContext';
import { useStrategy } from '../../contexts/StrategyContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface SaveStrategyDialogProps {
  blocks: Block[];
  onClose: () => void;
  onSaved: (strategyId: string) => void;
  templateId?: string; // Optional template ID if saving from a template
  strategyId?: string; // Optional strategy ID if updating an existing strategy
  // S89 — force a "Save As" (always create a NEW record) even when the user
  // owns the loaded template. The toolbar splits the two actions: the
  // dedicated "Update Template" button overwrites the loaded template in
  // place; this dialog — opened by the "Save As" button — always saves a NEW
  // copy so the original template stays intact.
  forceSaveAs?: boolean;
}

const SaveStrategyDialog: React.FC<SaveStrategyDialogProps> = ({
  blocks,
  onClose,
  onSaved,
  templateId,
  strategyId,
  forceSaveAs = false,
}) => {
  useEscapeKey(onClose);
  const { user } = useAuth();
  const { renameCurrent } = useStrategy();
  const [metadata, setMetadata] = useState({
    name: '',
    description: '',
    platform: TradingPlatform.MT5,
    assetClass: AssetClass.FOREX,
    version: '1.0.0',
    tags: '',
    isPublic: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthor, setIsAuthor] = useState(false);
  const [mode, setMode] = useState<'loading' | 'create' | 'update' | 'template' | 'copy'>('loading');

  useEffect(() => {
    let ignore = false;

    const checkAuthorAndSetMode = async () => {
      // S89 — "Save As" always creates a NEW record (a copy of the template),
      // even for the template's author. In-place overwrite is the separate
      // "Update Template" toolbar button.
      if (forceSaveAs && templateId) {
        try {
          const template = await getTemplate(templateId);
          if (ignore) return;
          if (template) {
            setMetadata({
              name: `${template.name} (copy)`,
              description: template.description || '',
              platform: template.platform,
              assetClass: template.assetClass,
              version: template.version,
              tags: template.tags?.join(', ') || '',
              isPublic: false, // a fresh copy starts private
            });
          }
        } catch (err) {
          console.error('Error fetching template for Save As:', err);
        }
        if (!ignore) setMode('copy');
        return;
      }
      // Determine the mode based on provided IDs
      if (!templateId && !strategyId) {
        setMode('create');
        return;
      }

      if (strategyId && typeof strategyId === 'string' && strategyId.trim() !== '') {
        setMode('update');
        return;
      }

      if (templateId) {
        try {
          // If we have a templateId, check if the current user is the author
          const authorStatus = await isTemplateAuthor(templateId);
          if (ignore) return;
          
          setIsAuthor(authorStatus);
          setMode(authorStatus ? 'update' : 'copy');
          
          // Fetch template details to populate the form
          const template = await getTemplate(templateId);
          if (ignore) return;
          
          if (template) {
            console.log("Fetched template:", template);
            setMetadata({
              name: template.name,
              description: template.description || '',
              platform: template.platform,
              assetClass: template.assetClass,
              version: template.version,
              tags: template.tags?.join(', ') || '',
              isPublic: template.isPublic || false,
            });
          }
        } catch (err) {
          console.error("Error checking template authorship:", err);
          if (!ignore) {
            setIsAuthor(false);
            setMode('copy');
          }
        }
      }
    };

    checkAuthorAndSetMode();

    return () => { ignore = true; };
  }, [templateId, strategyId, forceSaveAs]);

  const handleSave = async () => {
    if (!metadata.name.trim()) {
      setError('Strategy name is required');
      return;
    }

    if (!user) {
      setError('You must be logged in to save a strategy');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let resultId: string;

      if (mode === 'update' && templateId && isAuthor) {
        // Update existing template
        console.log("Updating template with ID:", templateId);
        console.log("Update data:", {
          name: metadata.name,
          description: metadata.description,
          blocks,
          platform: metadata.platform,
          assetClass: metadata.assetClass,
          version: metadata.version,
          tags: metadata.tags.split(',').map(tag => tag.trim()).filter(Boolean),
          isPublic: metadata.isPublic
        });
        
        const updatedTemplate = await updateTemplate(templateId, {
          name: metadata.name,
          description: metadata.description,
          blocks,
          platform: metadata.platform,
          assetClass: metadata.assetClass,
          version: metadata.version,
          tags: metadata.tags.split(',').map(tag => tag.trim()).filter(Boolean),
          isPublic: metadata.isPublic
        });
        
        console.log("Template updated successfully:", updatedTemplate);
        resultId = templateId;
      } else if (mode === 'copy' && templateId) {
        // Create a copy of the template
        resultId = await copyTemplate(templateId, {
          name: metadata.name,
          description: metadata.description,
          isPublic: metadata.isPublic
        });
      } else if (mode === 'update' && strategyId) {
        // Validate strategyId before update
        if (typeof strategyId !== 'string' || !strategyId.trim()) {
          throw new Error('Invalid strategy ID for update operation');
        }
        // Update existing strategy (which is now a template)
        await updateStrategy(strategyId, blocks, {
          ...metadata,
          tags: metadata.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        });
        resultId = strategyId;
      } else {
        // Create new strategy (as a template)
        resultId = await saveStrategy(blocks, {
          ...metadata,
          tags: metadata.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        });
      }

      // Reflect the saved name in the active-strategy header immediately.
      // rename-only (no loadedAt bump) so the canvas doesn't re-arrange.
      renameCurrent(metadata.name.trim());

      onSaved(resultId);
      onClose();
    } catch (err) {
      console.error("Save error:", err);
      setError(err instanceof Error ? err.message : 'Failed to save strategy');
    } finally {
      setSaving(false);
    }
  };

  const getDialogTitle = () => {
    if (mode === 'loading') {
      return 'Loading...';
    } else if (mode === 'update') {
      return templateId ? 'Update Template' : 'Update Strategy';
    } else if (mode === 'copy') {
      return 'Create a Copy';
    } else {
      return 'Save Strategy';
    }
  };

  const getButtonText = () => {
    if (saving) {
      return mode === 'update' 
        ? (templateId ? 'Updating...' : 'Updating...') 
        : (mode === 'copy' ? 'Creating Copy...' : 'Saving...');
    } else {
      return mode === 'update'
        ? (templateId ? 'Update Template' : 'Update Strategy')
        : (mode === 'copy' ? 'Create a Copy' : 'Save Strategy');
    }
  };

  if (mode === 'loading') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center">
            <RefreshCw className="w-5 h-5 mr-2 animate-spin text-blue-600" />
            <p>Checking permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-medium text-gray-800">{getDialogTitle()}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Strategy Name
              </label>
              <input
                type="text"
                value={metadata.name}
                onChange={(e) => setMetadata({ ...metadata, name: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="My Trading Strategy"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={metadata.description}
                onChange={(e) => setMetadata({ ...metadata, description: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Describe your strategy..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Platform
                </label>
                <select
                  value={metadata.platform}
                  onChange={(e) => setMetadata({ 
                    ...metadata, 
                    platform: e.target.value as TradingPlatform 
                  })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.values(TradingPlatform).map((platform) => (
                    <option key={platform} value={platform}>{platform}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asset Class
                </label>
                <select
                  value={metadata.assetClass}
                  onChange={(e) => setMetadata({ 
                    ...metadata, 
                    assetClass: e.target.value as AssetClass 
                  })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.values(AssetClass).map((assetClass) => (
                    <option key={assetClass} value={assetClass}>{assetClass}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Version
              </label>
              <input
                type="text"
                value={metadata.version}
                onChange={(e) => setMetadata({ ...metadata, version: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1.0.0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <input
                type="text"
                value={metadata.tags}
                onChange={(e) => setMetadata({ ...metadata, tags: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="trend-following, forex, scalping"
              />
              <p className="mt-1 text-xs text-gray-500">
                Separate tags with commas
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                checked={metadata.isPublic}
                onChange={(e) => setMetadata({ ...metadata, isPublic: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="isPublic" className="ml-2 text-sm text-gray-700">
                Make this {mode === 'update' && templateId ? 'template' : 'strategy'} public
              </label>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700 text-sm">
              <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex items-center px-4 py-2 rounded-md text-white font-medium transition-colors ${
                saving
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1.5" />
                  {getButtonText()}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveStrategyDialog;